import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOutletContext } from 'react-router-dom';
import { useInventory } from '../context/InventoryContext';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import { FaDownload, FaEye, FaUserCheck, FaTimes, FaPlus } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import api from "../services/api";

export default function QueryPage() {
  const { currentUser, users = [] } = useAuth() || {};
  
  const {
    companies = [],
    subCompanies = [],
    vehicles = []
  } = useInventory() || {};

  const userRole = currentUser?.role || ""; 
  const currentCompanyId = currentUser?.companyId || null;

  const isTeamLead = userRole === 'teamlead';

  const { queriesList: layoutQueries } = useOutletContext() || {};

  // ✅ Get assigned installer IDs for teamlead - FIXED
  const assignedInstallerIds = useMemo(() => {
    if (!isTeamLead || !users || !currentUser) return [];
    return users
      .filter(u => u.role === 'installer' && String(u.teamLeadId) === String(currentUser.id))
      .map(u => u.id);
  }, [users, isTeamLead, currentUser]);

  // ✅ Get assigned installer usernames for teamlead - FIXED
  const assignedInstallerUsernames = useMemo(() => {
    if (!isTeamLead || !users || !currentUser) return [];
    return users
      .filter(u => u.role === 'installer' && String(u.teamLeadId) === String(currentUser.id))
      .map(u => String(u.username || '').toLowerCase());
  }, [users, isTeamLead, currentUser]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editId, setEditId] = useState(null); 
  
  const [singleVehicleSearch, setSingleVehicleSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  
  const searchFilter = '';
  const statusFilter = '';
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const [dateFilter, setDateFilter] = useState('7days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [installerFilter, setInstallerFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [uniqueInstallers, setUniqueInstallers] = useState([]);
  const [uniqueCompanies, setUniqueCompanies] = useState([]);

  const [showVehiclePopup, setShowVehiclePopup] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [popupInstallerFilter, setPopupInstallerFilter] = useState('');
  const [popupStatusFilter, setPopupStatusFilter] = useState('');
  const [popupCurrentQueryId, setPopupCurrentQueryId] = useState(null);
  const [popupCurrentVehicleList, setPopupCurrentVehicleList] = useState([]);

  const [queriesList, setQueriesList] = useState([]);

  const isAdmin = userRole === 'admin';
  const isSuperAdmin = userRole === 'super_admin';

  // ✅ State for custom query categories
  const [customCategories, setCustomCategories] = useState([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // ✅ State for action filter (Completed/Pending only)
  const [actionFilter, setActionFilter] = useState('');

  // ✅ Get the team lead's installers for dropdown
  const teamLeadInstallers = useMemo(() => {
    if (!isTeamLead || !users || !currentUser) return [];
    return users.filter(u => 
      u.role === 'installer' && 
      String(u.teamLeadId) === String(currentUser.id)
    );
  }, [users, isTeamLead, currentUser]);

  // ✅ Load custom categories from localStorage
  useEffect(() => {
    const savedCategories = localStorage.getItem('customQueryCategories');
    if (savedCategories) {
      try {
        setCustomCategories(JSON.parse(savedCategories));
      } catch (e) {
        console.error('Error loading custom categories:', e);
      }
    }
  }, []);

  // ✅ Save custom categories to localStorage
  const saveCustomCategory = (category) => {
    const updated = [...customCategories, category];
    setCustomCategories(updated);
    localStorage.setItem('customQueryCategories', JSON.stringify(updated));
    toast.success(`Category "${category}" added successfully!`);
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) {
      toast.warning('Please enter a category name');
      return;
    }
    
    // Check if category already exists
    const allCategories = ['Panic Issue', 'GPS Issue', 'GPS Removal', 'Main Power Discontinue Issue', 'SIM Issue', 'New Installation', 'Re Installation', 'Others', ...customCategories];
    if (allCategories.some(cat => cat.toLowerCase() === newCategoryName.trim().toLowerCase())) {
      toast.warning('Category already exists');
      return;
    }
    
    saveCustomCategory(newCategoryName.trim());
    setNewCategoryName('');
    setIsAddingCategory(false);
  };

  useEffect(() => {
    const handleOpenSerialModal = (event) => {
      const ticketNo = event?.detail?.ticketNo || null;
      if (ticketNo) {
        localStorage.setItem("pendingSerialQueryId", ticketNo);
      }
      localStorage.setItem("pendingSerialAction", "complete");
      window.dispatchEvent(new CustomEvent("serialModalRequested"));
    };
    window.addEventListener("openSerialModal", handleOpenSerialModal);
    return () => {
      window.removeEventListener("openSerialModal", handleOpenSerialModal);
    };
  }, []);

  const updateFilters = (queries) => {
    const installers = [...new Set(queries.map(q => q.installerName).filter(Boolean))].sort();
    setUniqueInstallers(installers);
    const companies = [...new Set(queries.map(q => q.companyName).filter(Boolean))].sort();
    setUniqueCompanies(companies);
  };

  // ✅ FIXED: Load queries with Team Lead filtering
  const loadQueriesFromBackend = useCallback(async () => {
    if (!userRole) return;
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || "https://cccs.cordonnx.com";
      const activeUsername = currentUser?.username || currentUser?.name || '';
      
      let url = `${API_BASE_URL}/api/queries?role=${userRole}&companyId=${currentCompanyId}&username=${encodeURIComponent(activeUsername)}`;
      
      // ✅ ADD Team Lead ID to the API request
      if (isTeamLead && currentUser) {
        url += `&teamLeadId=${currentUser.id}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (Array.isArray(data)) {
        let formattedData = data.map(q => ({
          ...q,
          ticketNo: q.ticket_no || q.ticketNo,
          companyId: q.company_id || q.companyId,     
          companyName: q.company_name || q.companyName,
          subCompanyName: q.sub_company_name || q.subCompanyName,
          installerName: q.installer_name || q.installerName,
          vehicleName: q.vehicle_name || q.vehicleName,
          vehicleCount: q.vehicle_count || q.vehicleCount,
          queryType: q.query_type || q.queryType,
          serialNumber: q.serial_number || q.serialNumber,
          installationDate: q.installation_date || q.installationDate,
          createdAt: q.created_at || q.createdAt,
          updatedAt: q.updated_at || q.updatedAt,
          vehicleIds: q.vehicle_ids || q.vehicleIds,
          installerId: q.installer_id || q.installerId // ✅ Added for filtering
        }));
        
        // ✅ FALLBACK: Client-side filter for Team Lead (if backend doesn't filter)
        if (isTeamLead) {
          formattedData = formattedData.filter(query => {
            // Check by installer_id
            if (query.installerId && assignedInstallerIds.includes(query.installerId)) {
              return true;
            }
            // Check by installer_name
            if (query.installerName && assignedInstallerUsernames.includes(String(query.installerName).toLowerCase())) {
              return true;
            }
            return false;
          });
        }
        
        setQueriesList(formattedData);
        updateFilters(formattedData);
        console.log(`✅ LogPage loaded ${formattedData.length} queries for ${userRole}`);
      }
    } catch (error) {
      console.error('Error fetching data from PostgreSQL Backend:', error);
    }
  }, [userRole, currentCompanyId, currentUser, isTeamLead, assignedInstallerIds, assignedInstallerUsernames]);

  // ========== AUTOMATIC LIVE SYNC FROM DATABASE LOOP ==========
  useEffect(() => {
    if (!userRole) return;

    // 💡 If the user is a Team Lead and we have layoutQueries, use them
    if (isTeamLead && layoutQueries && Array.isArray(layoutQueries)) {
      console.log("📊 LogPage using Layout context data for Team Lead:", layoutQueries.length);
      
      // ✅ Apply Team Lead filter to layoutQueries
      const filteredLayoutQueries = layoutQueries.filter(query => {
        if (query.installerId && assignedInstallerIds.includes(query.installerId)) {
          return true;
        }
        if (query.installerName && assignedInstallerUsernames.includes(String(query.installerName).toLowerCase())) {
          return true;
        }
        return false;
      });
      
      setQueriesList(filteredLayoutQueries);
      updateFilters(filteredLayoutQueries);
      return;
    }

    // For Admin / Super Admin
    loadQueriesFromBackend();

    const interval = setInterval(() => {
      loadQueriesFromBackend();
    }, 5000);

    return () => clearInterval(interval);
  }, [loadQueriesFromBackend, userRole, layoutQueries, isTeamLead, assignedInstallerIds, assignedInstallerUsernames]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, statusFilter, dateFilter, installerFilter, companyFilter, actionFilter]);

  const [formData, setFormData] = useState({
    companyId: '',
    subCompanyId: '',
    vehicleSelectionMode: '', 
    vehicleId: '',
    multipleVehiclesText: '',
    installerId: '',
    queryType: '',
    otherDescription: ''
  });

  const formatDateToLocal = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} - ${hours}:${minutes}`;
    } catch (e) {
      return dateString;
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      toast.info("Please enter a Ticket ID or Vehicle Number to search");
      return;
    }
    setIsSearching(true);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setIsSearching(false);
    setCurrentPage(1);
  };

  const applySearchFilter = useCallback((queries) => {
    if (!searchTerm.trim() || !isSearching) return queries;
    const term = searchTerm.trim().toLowerCase();
    return queries.filter(query => {
      const ticketMatch = query.ticketNo && query.ticketNo.toLowerCase().includes(term);
      const vehicleMatch = query.vehicleName && query.vehicleName.toLowerCase().includes(term);
      return ticketMatch || vehicleMatch;
    });
  }, [searchTerm, isSearching]);

  const applyDateFilter = useCallback((queries) => {
    if (!dateFilter) return queries;
    const now = new Date();
    let startDateObj = null;
    let endDateObj = new Date();
    if (dateFilter === '7days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 7);
    } else if (dateFilter === '15days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 15);
    } else if (dateFilter === '1month') {
      startDateObj = new Date();
      startDateObj.setMonth(now.getMonth() - 1);
    } else if (dateFilter === '3months') {
      startDateObj = new Date();
      startDateObj.setMonth(now.getMonth() - 3);
    } else if (dateFilter === 'custom' && startDate && endDate) {
      startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0);
      endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999);
    }
    if (startDateObj) {
      return queries.filter(q => {
        const queryDate = new Date(q.createdAt);
        if (dateFilter === 'custom') {
          return queryDate >= startDateObj && queryDate <= endDateObj;
        }
        return queryDate >= startDateObj;
      });
    }
    return queries;
  }, [dateFilter, startDate, endDate]);

  const applyInstallerFilter = useCallback((queries) => {
    if (!installerFilter) return queries;
    return queries.filter(q => 
      String(q.installerName || '').toLowerCase().trim() === String(installerFilter).toLowerCase().trim()
    );
  }, [installerFilter]);

  const applyCompanyFilter = useCallback((queries) => {
    if (!companyFilter) return queries;
    return queries.filter(q => 
      String(q.companyName || '').toLowerCase().trim() === String(companyFilter).toLowerCase().trim()
    );
  }, [companyFilter]);

  // ✅ Apply action filter (Completed/Pending only)
  const applyActionFilter = useCallback((queries) => {
    if (!actionFilter) return queries;
    return queries.filter(q => {
      if (actionFilter === 'completed') {
        return q.status === 'Completed';
      }
      if (actionFilter === 'pending') {
        return q.status === 'Pending' || q.status === 'Ongoing';
      }
      return true;
    });
  }, [actionFilter]);

  const handleDownloadExcel = () => {
    const dataToExport = filteredQueries.map(q => ({
      'Ticket ID': q.ticketNo || '',
      'Company': q.companyName || '',
      'Sub Company': q.subCompanyName || 'N/A',
      'Vehicle Count': q.vehicleCount || 0,
      'Installer': q.installerName || 'N/A',
      'Category': q.queryType || '',
      'Description': q.description || '',
      'Status': q.status || '',
      'Serial Number': q.serialNumber || 'N/A',
      'Query Updated Date': q.installationDate ? formatDateToLocal(q.installationDate) : 'N/A',
      'Created Time': formatDateToLocal(q.createdAt),
      'Updated Time': formatDateToLocal(q.updatedAt || q.createdAt)
    }));

    if (dataToExport.length === 0) {
      toast.warning("No data available to download!");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    ws['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 40 }, { wch: 15 },
      { wch: 20 }, { wch: 22 }, { wch: 22 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Query Logs');
    const fileName = `Query_Logs_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("File downloaded successfully!");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'companyId') {
        updated.subCompanyId = '';
        updated.vehicleSelectionMode = '';
        updated.vehicleId = '';
        updated.multipleVehiclesText = '';
        setSingleVehicleSearch('');
      }
      if (name === 'subCompanyId') {
        updated.vehicleSelectionMode = '';
        updated.vehicleId = '';
        updated.multipleVehiclesText = '';
        setSingleVehicleSearch('');
      }
      if (name === 'vehicleSelectionMode') {
        updated.vehicleId = '';
        updated.multipleVehiclesText = '';
        setSingleVehicleSearch('');
      }
      return updated;
    });
  };

  const finalInstallers = useMemo(() => {
    // ✅ For Team Lead, only show their assigned installers
    if (isTeamLead) {
      return teamLeadInstallers;
    }
    return users.filter(u => u.role === 'installer');
  }, [users, isTeamLead, teamLeadInstallers]);

  const availableSubCompanies = useMemo(() => {
    if (!formData.companyId) return [];
    const parentCompanyObj = companies.find(c => String(c.id) === String(formData.companyId));
    if (!parentCompanyObj) return [];
    return subCompanies.filter(s => s.parentCompanyName === parentCompanyObj.name);
  }, [formData.companyId, companies, subCompanies]);

  // ✅ MODIFIED: Show vehicles when company is selected even without sub-company
  const availableVehicles = useMemo(() => {
    if (!formData.companyId) return [];
    return vehicles.filter(v => {
      // If sub-company is selected, filter by sub-company
      if (formData.subCompanyId) {
        return String(v.subCompanyId) === String(formData.subCompanyId);
      }
      // If only company is selected (no sub-company), show vehicles from the company
      return String(v.parentCompanyId) === String(formData.companyId) && 
             (!v.subCompanyId || v.subCompanyId === "0" || v.subCompanyId === "");
    });
  }, [formData.companyId, formData.subCompanyId, vehicles]);

  const filteredSingleVehiclesList = useMemo(() => {
    if (!singleVehicleSearch.trim()) return availableVehicles;
    const cleanSearchStr = singleVehicleSearch.trim().toLowerCase();
    return availableVehicles.filter(v => 
      (v.licensePlate || "").toLowerCase().includes(cleanSearchStr) ||
      String(v.id).toLowerCase().includes(cleanSearchStr)
    );
  }, [availableVehicles, singleVehicleSearch]);

  const resolvedActiveSingleVehicleLabel = useMemo(() => {
    const matchFound = availableVehicles.find(v => String(v.id) === String(formData.vehicleId));
    return matchFound ? matchFound.licensePlate : '';
  }, [formData.vehicleId, availableVehicles]);

  const handleVerifyVehicles = (e) => {
    if (e) e.preventDefault();
    if (!formData.multipleVehiclesText.trim()) {
      toast.warning("Please enter vehicle trailing digits first!");
      return null;
    }

    const inputs = formData.multipleVehiclesText
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    const matchedPlates = [];
    const failedInputs = [];

    inputs.forEach(input => {
      const inputCleaned = input.replace(/\s+/g, '').toLowerCase();
      const matchFound = availableVehicles.find(v => {
        const actualPlateCleaned = (v.licensePlate || "").replace(/\s+/g, '').toLowerCase();
        return actualPlateCleaned.endsWith(inputCleaned) && inputCleaned.length > 0;
      });
      if (matchFound) {
        matchedPlates.push(matchFound.licensePlate);
      } else {
        failedInputs.push(input);
      }
    });

    if (failedInputs.length > 0) {
      toast.error(`Error: The following entered sequences are not found: ${failedInputs.join(', ')}`);
      return null;
    }

    const fullPlatesString = matchedPlates.join(', ');
    setFormData(prev => ({ ...prev, multipleVehiclesText: fullPlatesString }));
    toast.success(`Success! Linked ${matchedPlates.length} vehicles.`);
    return fullPlatesString;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let selectedCompanyId = formData.companyId || currentCompanyId;

    let vehicleIds = [];
    if (formData.vehicleSelectionMode === "single") {
      vehicleIds = [formData.vehicleId].filter(Boolean); 
    } else {
      vehicleIds = vehicles
        .filter(v => formData.multipleVehiclesText.split(",").map(x => x.trim()).includes(v.licensePlate))
        .map(v => v.id);
    }

    const payload = {
      id: editId, 
      company_id: selectedCompanyId,
      company_name: companies.find(c => String(c.id) === String(selectedCompanyId))?.name || '',
      sub_company_name: subCompanies.find(s => String(s.id) === String(formData.subCompanyId))?.name || 'N/A',
      installer_name: finalInstallers.find(i => String(i.id) === String(formData.installerId))?.username || 'N/A',
      vehicle_name: formData.multipleVehiclesText || availableVehicles.find(v => String(v.id) === String(formData.vehicleId))?.licensePlate || '',
      vehicle_count: formData.vehicleSelectionMode === "single" ? 1 : formData.multipleVehiclesText.split(',').length || 0,
      query_type: formData.queryType,
      description: formData.queryType === "Others" ? formData.otherDescription : formData.queryType,
      vehicle_ids: vehicleIds
    };

    try {
      const response = await fetch('https://cccs.cordonnx.com/api/queries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const resData = await response.json();
      if (resData.success || response.ok) {
        toast.success(editId ? "Query Log Updated" : "Query Log Created");
        loadQueriesFromBackend(); 
        setIsModalOpen(false);
        setFormData({ companyId: "", subCompanyId: "", vehicleSelectionMode: "", vehicleId: "", multipleVehiclesText: "", installerId: "", queryType: "", otherDescription: "" });
        setEditId(null);
      }
    } catch (error) {
      toast.error("Database operation failed.");
    }
  };

  const handleEditClick = (query) => {
    setEditId(query.id);
    const matchCompany = companies.find(c => c.name === query.companyName);
    const targetCompId = matchCompany ? String(matchCompany.id) : query.companyId;
    const matchedInstaller = finalInstallers.find(i => i.username === query.installerName);
    const resolvedInstallerId = matchedInstaller ? String(matchedInstaller.id) : '';
    const matchedVehicle = vehicles.find(v => v.licensePlate === query.vehicleName);
    const isMultiple = query.vehicleName && query.vehicleName.includes(',');

    setFormData({
      companyId: targetCompId,
      subCompanyId: '', 
      vehicleSelectionMode: isMultiple ? 'multiple' : matchedVehicle ? 'single' : 'single',
      vehicleId: matchedVehicle ? String(matchedVehicle.id) : '',
      multipleVehiclesText: isMultiple ? query.vehicleName : '',
      installerId: resolvedInstallerId,
      queryType: query.queryType,
      otherDescription: query.queryType === 'Others' ? query.description : ''
    });
    setSingleVehicleSearch(matchedVehicle ? matchedVehicle.licensePlate : '');
    setIsModalOpen(true);
  };

  const openVehiclePopup = (query) => {
    const vehicleList = query.vehicleName
      ? query.vehicleName.split(",").map(v => v.trim())
      : [];

    setPopupCurrentVehicleList(vehicleList);
    setPopupCurrentQueryId(query.id);
    setSelectedVehicles(vehicleList);
    setPopupInstallerFilter("");
    setPopupStatusFilter("");
    setShowVehiclePopup(true);
  };

  const toggleVehicleSelection = (vehicle) => {
    setSelectedVehicles(prev => {
      if (prev.includes(vehicle)) {
        return prev.filter(v => v !== vehicle);
      } else {
        return [...prev, vehicle];
      }
    });
  };

  const handleAssignInstallerForSelected = async () => {
    if (!isSuperAdmin) {
      toast.warning("Only Super Admin can assign vehicles.");
      return;
    }

    if (!popupInstallerFilter) {
      toast.warning("Please select an installer");
      return;
    }

    if (selectedVehicles.length === 0) {
      toast.warning("Please select vehicles");
      return;
    }

    const currentQuery = queriesList.find(q => String(q.id) === String(popupCurrentQueryId));

    if (!currentQuery) {
      toast.error("Query not found");
      return;
    }

    const allVehicles = currentQuery.vehicleName
      ? currentQuery.vehicleName.split(",").map(v => v.trim())
      : [];

    const remainingVehicles = allVehicles.filter(
      v => !selectedVehicles.includes(v)
    );

    const selectedVehicleIds = selectedVehicles
      .map(v => {
        const vehicle = vehicles.find(x => x.licensePlate === v);
        return vehicle ? vehicle.id : null;
      })
      .filter(Boolean);

    try {
      const response = await fetch("https://cccs.cordonnx.com/api/queries/split-vehicles", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          currentQueryId: currentQuery.id,
          remainingVehicles: remainingVehicles.join(", "),
          remainingCount: remainingVehicles.length,
          selectedVehicles: selectedVehicles.join(", "),
          selectedCount: selectedVehicles.length,
          targetInstaller: popupInstallerFilter,
          targetStatus: "Pending",
          selectedVehicleIds,
          queryDetails: currentQuery
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        loadQueriesFromBackend();
        setShowVehiclePopup(false);
      } else {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error("Assignment failed.");
    }
  };

  const handleStatusUpdateForSelected = async () => {
    if (!popupStatusFilter) {
      toast.warning("Please select status");
      return;
    }

    if (selectedVehicles.length === 0) {
      toast.warning("Please select vehicles");
      return;
    }

    const currentQuery = queriesList.find(q => String(q.id) === String(popupCurrentQueryId));

    if (!currentQuery) {
      toast.error("Query not found");
      return;
    }

    const allVehicles = currentQuery.vehicleName
      ? currentQuery.vehicleName.split(",").map(v => v.trim())
      : [];

    const remainingVehicles = allVehicles.filter(
      v => !selectedVehicles.includes(v)
    );

    const selectedVehicleIds = selectedVehicles
      .map(v => {
        const vehicle = vehicles.find(x => x.licensePlate === v);
        return vehicle ? vehicle.id : null;
      })
      .filter(Boolean);

    try {
      if (popupStatusFilter === "Completed") {
        const response = await fetch("https://cordonnx.com/api/queries/split-vehicles", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            currentQueryId: currentQuery.id,
            remainingVehicles: remainingVehicles.join(", "),
            remainingCount: remainingVehicles.length,
            selectedVehicles: selectedVehicles.join(", "),
            selectedCount: selectedVehicles.length,
            targetInstaller: currentQuery.installerName,
            targetStatus: popupStatusFilter,
            selectedVehicleIds,
            queryDetails: currentQuery
          })
        });

        const data = await response.json();

        if (response.ok) {
          toast.success(data.message);
          loadQueriesFromBackend();
          setShowVehiclePopup(false);
        } else {
          toast.error(data.error);
        }
        return;
      }

      if (popupStatusFilter === "Incomplete") {
        const response = await fetch(`https://cordonnx.com/api/queries/${currentQuery.id}/vehicle-status`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            vehicleIds: selectedVehicleIds,
            status: "Pending"
          })
        });

        const data = await response.json();

        if (response.ok) {
          toast.success("Vehicle status updated to Incomplete/Pending");
          loadQueriesFromBackend();
          setShowVehiclePopup(false);
        } else {
          toast.error(data.error);
        }
        return;
      }

      const response = await fetch("https://cordonnx.com/api/queries/split-vehicles", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          currentQueryId: currentQuery.id,
          remainingVehicles: remainingVehicles.join(", "),
          remainingCount: remainingVehicles.length,
          selectedVehicles: selectedVehicles.join(", "),
          selectedCount: selectedVehicles.length,
          targetInstaller: currentQuery.installerName,
          targetStatus: popupStatusFilter,
          selectedVehicleIds,
          queryDetails: currentQuery
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        loadQueriesFromBackend();
        setShowVehiclePopup(false);
      }
    } catch (err) {
      toast.error("Status update failed.");
    }
  };

  const handleStatusChange = async (queryId, newStatus) => {
    try {
      const response = await fetch(`https://cordonnx.com/api/queries/${queryId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Status updated to ${newStatus} successfully!`);
        loadQueriesFromBackend();
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.error || "Backend failed to update status.");
      }
    } catch (err) {
      toast.error("Failed to submit status update.");
    }
  };

  const getStatusDropdownStyles = (status) => {
    switch (status) {
      case 'Pending': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'Ongoing': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Completed': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // ✅ FIXED: Filter queries for Team Lead
  const filteredQueries = useMemo(() => {
    let baseQueries = [];
    
    const loggedInInstallerName = currentUser?.username || currentUser?.name || "";

    if (userRole === 'super_admin') {
      baseQueries = queriesList;
    } else if (userRole === 'admin') {
      const adminCompanyId = String(currentCompanyId);
      baseQueries = queriesList.filter(query => String(query.companyId) === adminCompanyId);
    } else if (userRole === 'installer') {
      baseQueries = queriesList.filter(query => 
        query.installerName && 
        loggedInInstallerName && 
        query.installerName.trim().toLowerCase() === loggedInInstallerName.trim().toLowerCase()
      );
    } else if (userRole === 'teamlead') {
      // ✅ TeamLead - Show queries from their assigned installers
      baseQueries = queriesList.filter(query => {
        // Check by installer_id
        if (query.installerId && assignedInstallerIds.includes(query.installerId)) {
          return true;
        }
        // Check by installer_name
        if (query.installerName && assignedInstallerUsernames.includes(String(query.installerName).toLowerCase())) {
          return true;
        }
        return false;
      });
    } else {
      return [];
    }

    let filtered = applySearchFilter(baseQueries);
    filtered = applyDateFilter(filtered);
    
    if (isSuperAdmin || isAdmin) {
      filtered = applyInstallerFilter(filtered);
      filtered = applyCompanyFilter(filtered);
    }
    
    // ✅ Apply action filter
    filtered = applyActionFilter(filtered);
    
    return filtered;
  }, [
    userRole, 
    currentCompanyId, 
    queriesList, 
    applySearchFilter, 
    applyDateFilter, 
    applyInstallerFilter, 
    applyCompanyFilter,
    applyActionFilter,
    isSuperAdmin,
    isAdmin,
    currentUser,
    assignedInstallerIds,
    assignedInstallerUsernames
  ]);

  const paginatedQueries = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredQueries.slice(startIndex, endIndex);
  }, [filteredQueries, currentPage, pageSize]);

  const visibleParentOptions = useMemo(() => {
    if (userRole === 'admin') {
      const adminCompany = companies.find(c => String(c.id) === String(currentCompanyId));
      return adminCompany ? [adminCompany] : [];
    }
    return companies;
  }, [userRole, currentCompanyId, companies]);

  // ✅ Get all query categories including custom ones
  const allQueryCategories = useMemo(() => {
    const defaultCategories = ['Panic Issue', 'GPS Issue', 'GPS Removal', 'Main Power Discontinue Issue', 'SIM Issue', 'New Installation', 'Re Installation', 'Others'];
    return [...defaultCategories, ...customCategories];
  }, [customCategories]);

  useEffect(() => {
    if (isModalOpen && userRole === 'admin' && !formData.companyId) {
      setFormData(prev => ({
        ...prev,
        companyId: String(currentCompanyId)
      }));
    }
  }, [isModalOpen, userRole, currentCompanyId, formData.companyId]);

  const handleClearFilters = () => {
    dateFilter === '7days' ? void 0 : setDateFilter('7days');
    setStartDate('');
    setEndDate('');
    setInstallerFilter('');
    setCompanyFilter('');
    setActionFilter('');
    setSearchTerm('');
    setIsSearching(false);
    setCurrentPage(1);
  };

  // ✅ Updated access check - Allow TeamLead, Super Admin, Admin, Installer
  if (
    userRole !== "super_admin" &&
    userRole !== "admin" &&
    userRole !== "installer" &&
    userRole !== "teamlead"
  ) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-red-500 font-bold text-xl font-sans">
        Access Denied
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen text-gray-800 font-sans block">
      <div className="w-full flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Log Management</h1>
          <p className="text-xs text-gray-500 mt-1">Raise support issues and monitor installer dispatch responses</p>
        </div>
        {/* ✅ Show button for both Super Admin and Team Lead */}
        {(userRole === "super_admin" || userRole === "teamlead") && (
          <button
            onClick={() => { setEditId(null); setIsModalOpen(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-5 py-2.5 rounded-lg shadow-sm transition-all flex items-center gap-2"
          >
            <FaPlus className="text-xs" /> Raise New Call Log
          </button>
        )}
      </div>

      {/* ✅ Team Lead info banner */}
      {userRole === 'teamlead' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-blue-600 text-sm font-medium">👤 Showing logs for your assigned installers ({assignedInstallerIds.length} installers)</span>
          </div>
          <span className="text-xs text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
            {filteredQueries.length} logs found
          </span>
        </div>
      )}

      <div className="w-full bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search by Ticket ID or Vehicle Number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm font-bold"
              >
                ✕
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-6 py-2.5 rounded-lg shadow-sm transition-all"
            >
              🔍 Search
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-gray-600">Date:</label>
            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                if (e.target.value !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="7days">Last 7 Days</option>
              <option value="15days">Last 15 Days</option>
              <option value="1month">Last 1 Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {dateFilter === 'custom' && (
            <>
              <div className="flex items-center gap-2">
                <label className="text-xs font-bold text-gray-600">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-bold text-gray-600">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {(isSuperAdmin || isAdmin) && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600">Installer:</label>
              <select
                value={installerFilter}
                onChange={(e) => { setInstallerFilter(e.target.value); setCurrentPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white min-w-[120px]"
              >
                <option value="">All Installers</option>
                {uniqueInstallers.map(installer => (
                  <option key={installer} value={installer}>{installer}</option>
                ))}
              </select>
            </div>
          )}

          {(isSuperAdmin || isAdmin) && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600">Company:</label>
              <select
                value={companyFilter}
                onChange={(e) => { setCompanyFilter(e.target.value); setCurrentPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white min-w-[120px]"
              >
                <option value="">All Companies</option>
                {uniqueCompanies.map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </div>
          )}

          {/* ✅ Action Filter - Only Completed and Pending */}
          {(isSuperAdmin || isAdmin) && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600">Status:</label>
              <select
                value={actionFilter}
                onChange={(e) => { setActionFilter(e.target.value); setCurrentPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white min-w-[120px]"
              >
                <option value="">All Status</option>
                <option value="completed">✅ Completed</option>
                <option value="pending">⏳ Pending</option>
              </select>
            </div>
          )}

          {(dateFilter !== '7days' || installerFilter || companyFilter || actionFilter || isSearching) && (
            <button
              onClick={handleClearFilters}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium px-4 py-1.5 rounded-lg transition"
            >
              Clear Filters
            </button>
          )}

          <div className="ml-auto">
            <button
              onClick={handleDownloadExcel}
              disabled={filteredQueries.length === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition ${
                filteredQueries.length === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              <FaDownload /> Download
            </button>
          </div>
        </div>

        {isSearching && searchTerm && (
          <div className="mt-2 text-xs text-blue-600 font-medium">
            Showing results for: <span className="font-bold">"{searchTerm}"</span> ({filteredQueries.length} results found)
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative border border-gray-100 my-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{editId ? "Edit Ticket Logs" : "Create Ticket"}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Company *</label>
                <select
                  name="companyId"
                  required
                  value={formData.companyId}
                  onChange={handleInputChange}
                  disabled={userRole === 'admin'}
                  className={`w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none bg-white ${userRole === 'admin' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">-- Select Company --</option>
                  {visibleParentOptions.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {formData.companyId && availableSubCompanies.length > 0 && (
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Sub Company</label>
                  <select
                    name="subCompanyId"
                    value={formData.subCompanyId}
                    onChange={handleInputChange}
                    className="w-full border border-purple-300 rounded-lg p-2.5 text-sm outline-none bg-white focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">-- Select Sub Company --</option>
                    {availableSubCompanies.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* ✅ MODIFIED: Show vehicle selection when company is selected, even without sub-company */}
              {formData.companyId && (
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Vehicle Assignment Type *</label>
                  <select
                    name="vehicleSelectionMode"
                    required
                    value={formData.vehicleSelectionMode}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none bg-white"
                  >
                    <option value="">-- Choose Processing Mode --</option>
                    <option value="single">🚗 Select Single Vehicle</option>
                    <option value="multiple">🔢 Select Multiple Vehicles</option>
                  </select>
                </div>
              )}

              {formData.vehicleSelectionMode === 'single' && (
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Target Asset Vehicle *</label>
                  <input
                    type="text"
                    placeholder="🔍 Search vehicle plate numbers here..."
                    value={isDropdownOpen ? singleVehicleSearch : (resolvedActiveSingleVehicleLabel || singleVehicleSearch)}
                    onFocus={() => setIsDropdownOpen(true)}
                    onChange={(e) => {
                      setSingleVehicleSearch(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:border-blue-500 outline-none transition"
                  />
                  {isDropdownOpen && (
                    <div className="absolute z-50 w-full bg-white border border-gray-200 shadow-xl rounded-lg mt-1 max-h-48 overflow-y-auto divide-y divide-gray-100">
                      {filteredSingleVehiclesList.length === 0 ? (
                        <div className="p-3 text-xs text-gray-400 italic">No corresponding vehicles found in records</div>
                      ) : (
                        filteredSingleVehiclesList.map(v => (
                          <div
                            key={v.id}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, vehicleId: String(v.id) }));
                              setSingleVehicleSearch(v.licensePlate);
                              setIsDropdownOpen(false);
                            }}
                            className={`p-2.5 text-sm cursor-pointer hover:bg-blue-50 text-gray-700 transition font-medium ${String(v.id) === String(formData.vehicleId) ? 'bg-blue-100 font-bold text-blue-900' : ''}`}
                          >
                            {v.licensePlate || `Asset #${v.id}`}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  <input type="hidden" required value={formData.vehicleId} />
                </div>
              )}

              {formData.vehicleSelectionMode === 'multiple' && (
                <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-xs font-bold text-blue-900 uppercase">Vehicle Plates (Last 4 digits separated by comma) *</label>
                    <span className="text-[10px] text-blue-600 font-medium bg-blue-100 px-1.5 py-0.5 rounded">e.g. 1234, 5678</span>
                  </div>
                  <div className="flex gap-2">
                    <textarea
                      name="multipleVehiclesText"
                      required
                      rows="2.5"
                      value={formData.multipleVehiclesText}
                      onChange={handleInputChange}
                      placeholder="Enter trailing 4 numbers separated by commas..."
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm outline-none focus:border-blue-500 bg-white"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyVehicles}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-3 rounded-lg shadow-sm transition-all whitespace-nowrap"
                    >
                      🔍 Verify & Match Assets
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Assigned Installer</label>
                <select
                  name="installerId"
                  value={formData.installerId}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none bg-white"
                >
                  <option value="">-- Select Installer --</option>
                  {finalInstallers.map(i => (
                    <option key={i.id} value={i.id}>{i.username}</option>
                  ))}
                </select>
              </div>

              {/* ✅ Query Category with Add New button */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Query Category *</label>
                <div className="flex gap-2">
                  <select
                    name="queryType"
                    required
                    value={formData.queryType}
                    onChange={handleInputChange}
                    className="flex-1 border border-gray-300 rounded-lg p-2.5 text-sm outline-none bg-white"
                  >
                    <option value="">-- Select Issue Type --</option>
                    {allQueryCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setIsAddingCategory(!isAddingCategory)}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold text-xs px-3 rounded-lg shadow-sm transition-all flex items-center gap-1 whitespace-nowrap"
                  >
                    <FaPlus className="text-[10px]" /> Add New
                  </button>
                </div>
                
                {/* ✅ Add New Category Input */}
                {isAddingCategory && (
                  <div className="mt-2 flex gap-2 items-center bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Enter new category name..."
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-blue-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingCategory(false);
                        setNewCategoryName('');
                      }}
                      className="text-gray-500 hover:text-gray-700 text-xs font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {formData.queryType === 'Others' && (
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Specify Problem Description *</label>
                  <textarea
                    name="otherDescription"
                    required
                    rows="3"
                    value={formData.otherDescription}
                    onChange={handleInputChange}
                    placeholder="Provide specific internal device fault data..."
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-2 border-t mt-4">
                <button type="button" onClick={() => { setIsModalOpen(false); setEditId(null); setIsAddingCategory(false); setNewCategoryName(''); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium text-white shadow-sm">{editId ? "Update Log" : "Submit Query"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden block">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th className="p-4">Ticket ID</th>
                <th className="p-4">Company Name</th>
                <th className="p-4">Sub Company</th>
                <th className="p-4">Vehicle Count</th>
                <th className="p-4">Installer</th>
                <th className="p-4">Issue Category</th>
                <th className="p-4">Details / Descriptions</th>
                <th className="p-4">Created Time</th>
                <th className="p-4">Updated Time</th>
                <th className="p-4">FCR Numbers</th>
                <th className="p-4">Query Updated Date</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
              {paginatedQueries.length === 0 ? (
                <tr>
                  <td colSpan="12" className="p-8 text-center text-gray-400 italic">
                    {isSearching ? `No results found for "${searchTerm}"` : 
                     isTeamLead ? 'No logs found for your assigned installers.' :
                     userRole === 'admin' ? 'No call logs are registered for your company.' : 'No query logs logged onto management databases.'}
                  </td>
                </tr>
              ) : (
                paginatedQueries.map((query) => {
                  const isCompleted = query.status === 'Completed';
                  
                  return (
                    <tr key={query.ticketNo} className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 font-mono text-xs font-bold text-blue-600 whitespace-nowrap">{query.ticketNo}</td>
                      <td className="p-4 font-semibold">{query.companyName}</td>
                      <td className="p-4 text-xs font-medium text-purple-700">{query.subCompanyName || "N/A"}</td>
                      <td className="p-4">
                        <button
                          onClick={() => openVehiclePopup(query)}
                          className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-bold px-3 py-1.5 rounded-lg text-xs transition flex items-center gap-1"
                        >
                          <FaEye className="text-[10px]" /> {query.vehicleCount || 0}
                        </button>
                      </td>
                      <td className="p-4">{query.installerName}</td>
                      <td className="p-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-amber-50 text-amber-800 text-xs border border-amber-200 rounded-md font-medium inline-block">{query.queryType}</span>
                      </td>
                      <td className="p-4 text-xs text-gray-500 max-w-xs truncate" title={query.description}>{query.description}</td>
                      <td className="p-4 text-xs font-mono text-gray-500 whitespace-nowrap">{formatDateToLocal(query.createdAt)}</td>
                      <td className="p-4 text-xs font-mono text-indigo-600 font-semibold whitespace-nowrap">{formatDateToLocal(query.updatedAt || query.createdAt)}</td>
                      {/* UPDATED: FCR Numbers column - Only first FCR visible with hover for more */}
                      <td className="p-4 text-xs font-mono font-bold">
                        {query.fcr_numbers ? (
                          (() => {
                            try {
                              const fcrData = typeof query.fcr_numbers === 'string' 
                                ? JSON.parse(query.fcr_numbers) 
                                : query.fcr_numbers;
                              const fcrValues = Object.values(fcrData).filter(v => v && v !== '');
                              if (fcrValues.length === 0) {
                                return <span className="text-gray-400">—</span>;
                              }
                              
                              // Show only first FCR number visible
                              const firstFCR = fcrValues[0];
                              const remainingCount = fcrValues.length - 1;
                              
                              return (
                                <div className="relative group inline-flex items-center gap-1">
                                  <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-[10px] font-mono cursor-help whitespace-nowrap">
                                    {firstFCR}
                                    {remainingCount > 0 && (
                                      <span className="text-gray-500 ml-1">+{remainingCount}</span>
                                    )}
                                  </span>
                                  
                                  {/* Hover tooltip showing all FCR numbers - only show if more than one */}
                                  {remainingCount > 0 && (
                                    <div className="absolute z-10 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-2 min-w-[200px] shadow-lg left-0 top-full mt-1">
                                      <div className="font-semibold mb-1">All FCR Numbers:</div>
                                      <div className="flex flex-wrap gap-1">
                                        {fcrValues.map((fcr, idx) => (
                                          <span key={idx} className="bg-emerald-700 text-emerald-100 px-2 py-0.5 rounded text-[10px] font-mono">
                                            {fcr}
                                          </span>
                                        ))}
                                      </div>
                                      <div className="text-gray-400 text-[10px] mt-1">Total: {fcrValues.length}</div>
                                    </div>
                                  )}
                                </div>
                              );
                            } catch (e) {
                              return <span className="text-gray-400">—</span>;
                            }
                          })()
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4 text-xs font-mono">
                        {query.installationDate ? (
                          <span className="text-blue-600 bg-blue-50 px-2 py-1 rounded whitespace-nowrap">{formatDateToLocal(query.installationDate)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {userRole === "super_admin" && !isCompleted && (
                            <button
                              onClick={() => handleEditClick(query)}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 font-bold text-xs py-1 px-2.5 rounded-lg transition"
                            >
                              ✏️ Edit
                            </button>
                          )}

                          {(userRole === "super_admin" || userRole === "installer") ? (
                            isCompleted ? (
                              <span
                                className={`px-3 py-1 rounded-lg text-xs font-bold ${getStatusDropdownStyles(query.status)}`}
                              >
                                ✅ COMPLETED
                              </span>
                            ) : (
                              <select
                                value={query.status}
                                onChange={(e) => handleStatusChange(query.id, e.target.value)}
                                className={`p-1.5 border rounded-lg text-xs font-bold tracking-wide outline-none cursor-pointer transition-all ${getStatusDropdownStyles(query.status)}`}
                              >
                                <option value="Pending">⏳ PENDING</option>
                                <option value="Ongoing">⚙️ ONGOING</option>
                                <option value="Completed">✅ COMPLETED</option>
                              </select>
                            )
                          ) : (
                            <span
                              className={`px-3 py-1 rounded-lg text-xs font-bold ${getStatusDropdownStyles(query.status)}`}
                            >
                              {query.status}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredQueries.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {showVehiclePopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 relative border border-gray-100 my-8">
            <button
              onClick={() => setShowVehiclePopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <FaTimes size={20} />
            </button>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Vehicle Details</h2>
            <p className="text-sm text-gray-500 mb-4">Total Vehicles: {popupCurrentVehicleList.length}</p>

            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-bold text-gray-600 mb-1">Filter by Installer</label>
                <select
                  value={popupInstallerFilter}
                  onChange={(e) => setPopupInstallerFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Installers</option>
                  {uniqueInstallers.map(installer => (
                    <option key={installer} value={installer}>{installer}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-bold text-gray-600 mb-1">Filter by Status</label>
                <select
                  value={popupStatusFilter}
                  onChange={(e) => setPopupStatusFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Completed">Completed</option>
                  <option value="Incomplete">Incomplete</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              {isSuperAdmin && (
                <button
                  onClick={handleAssignInstallerForSelected}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition flex items-center gap-1"
                >
                  <FaUserCheck /> Assign Selected
                </button>
              )}
              <button
                onClick={handleStatusUpdateForSelected}
                className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition flex items-center gap-1"
              >
                <FaUserCheck /> Update Status
              </button>
            </div>

            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-xs font-bold text-gray-600">Select</th>
                    <th className="p-2 text-xs font-bold text-gray-600">#</th>
                    <th className="p-2 text-xs font-bold text-gray-600">Vehicle Number</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {popupCurrentVehicleList.map((vehicle, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedVehicles.includes(vehicle)}
                          onChange={() => toggleVehicleSelection(vehicle)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="p-2 text-xs font-medium text-gray-600">{index + 1}</td>
                      <td className="p-2 text-sm font-medium text-gray-700">{vehicle}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {selectedVehicles.length} of {popupCurrentVehicleList.length} vehicles selected
              </span>
              <button
                onClick={() => setShowVehiclePopup(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}